import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Input, Button, Form, Tooltip, Drawer, TreeSelect, Select, Tag, Spin, message, Switch } from 'antd';
import { FilterOutlined, LoadingOutlined, ArrowRightOutlined, CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import * as Utilities from '../../Utilities';
import * as Styles from '../../../../Styles';
import FileConstants from '../../../../FileConstants';

import './SearchItem.css';
import MaterialList from "./MaterialList";
import DottedLoader from "../../DottedLoader";

const categoryStyle = Styles.categoryStyle;
const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
        color: "black",
      }}
      spin
    />
  );

const MaterialSearch = () => {
    const pageSize = 100;
    const [searchValue, setSearchValue] = useState('');
    const [filtersDrawer, setFiltersDrawer] = useState(false);
    const [materialCategoriesData, setMaterialCategoriesData] = useState([]);
    const [materialCategoryValue, setMaterialCategoryValue] = useState([]);
    const [modelStatus, setModelStatus] = useState(window.location.pathname === '/artist_search_item' ? [] : [5]);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [appliedFiltersList, setAppliedFiltersList] = useState([]);
    const [, updateState] = React.useState();
    const forceUpdate = React.useCallback(() => updateState({}), []);
    const [offset, setOffset] = useState(0);
    const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
    const [materials, setMaterials] = useState([]);
    const [allDataLoaded, setAllDataLoaded] = useState(false);
    const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
    const [selectedAdd, setSelectedAdd] = useState([]);
    const [selectedRemove, setSelectedRemove] = useState([]);
    const [selectedElementDetials, setSelectedElementDetials] = useState([]);
    const [actionLoader, setActionLoader] = useState(false);
    const [visibleToArtist, setVisibleToArtist] = useState(false);
    const artistSearchMaterial = window.location.pathname === '/artist_search_item';
    const [isRestoringFilters, setIsRestoringFilters] = useState(true);
    const defaultModelStatus = artistSearchMaterial ? [] : [5];
    const [initialStatusInUrl, setInitialStatusInUrl] = useState(null);
    const currentRequestRef = useRef(null);

    useEffect(() => {
        setIsLoadingMaterials(true);
        // capture whether status existed in the URL initially
        const params = new URLSearchParams(window.location.search);
        setInitialStatusInUrl(params.has('status'));
        setMaterialCategoryData();
    }, []);

    // After categories load, restore filters from URL (tab=material) then fetch using overrides
    useEffect(() => {
        if (materialCategoriesData.length === 0) return;
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get('tab');
        if (tabFromUrl !== 'material') {
            fetchMaterials(true);
            setIsRestoringFilters(false);
            return;
        }

        const searchFromUrl = params.get('search');
        const categoryFromUrl = params.get('category');
        const statusFromUrl = params.get('status');
        const visibleFromUrl = params.get('visible_to_artist');

        const updates = [];
        if (searchFromUrl) updates.push(() => setSearchValue(searchFromUrl));
        if (categoryFromUrl) updates.push(() => setMaterialCategoryValue(categoryFromUrl.split(',')));
        if (statusFromUrl) updates.push(() => setModelStatus(statusFromUrl.split(',').map(s => parseInt(s))));
        if (visibleFromUrl === 'true') updates.push(() => setVisibleToArtist(true));
        updates.forEach(u => u());

        const overrides = {};
        if (categoryFromUrl) overrides.materialCategoryValue = categoryFromUrl.split(',');
        if (statusFromUrl) overrides.modelStatus = statusFromUrl.split(',').map(s => parseInt(s));
        if (visibleFromUrl === 'true') overrides.visibleToArtist = true;

        const initialSearch = searchFromUrl ? searchFromUrl : undefined;
        const fetchPromise = fetchMaterials(true, initialSearch, overrides);
        if (fetchPromise && fetchPromise.finally) {
            fetchPromise.finally(() => setIsRestoringFilters(false));
        } else {
            setIsRestoringFilters(false);
        }
    }, [materialCategoriesData]);

    const saveFiltersToURL = () => {
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get('tab');
        if (tabFromUrl !== 'material') return;

        // Save search
        if (searchValue) params.set('search', searchValue); else params.delete('search');
        // Save category
        if (materialCategoryValue.length > 0) params.set('category', materialCategoryValue.join(',')); else params.delete('category');
        // Save status (avoid default unless initially present)
        if (modelStatus.length > 0) {
            if (!(Array.isArray(defaultModelStatus) && modelStatus.length === defaultModelStatus.length && modelStatus.every((v, i) => v === defaultModelStatus[i])) || initialStatusInUrl) {
                params.set('status', modelStatus.join(','));
            } else {
                params.delete('status');
            }
        } else {
            params.delete('status');
        }
        // Save visible
        if (visibleToArtist) params.set('visible_to_artist', 'true'); else params.delete('visible_to_artist');

        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }

    useEffect(() => {
        if (!isRestoringFilters) saveFiltersToURL();
    }, [materialCategoryValue, modelStatus, visibleToArtist, isRestoringFilters, searchValue]);

    useEffect(() => {
        //display tags when this is clicked
        if (materialCategoryValue.length > 0
            || (modelStatus.length > 0)
            || visibleToArtist) {
            setFiltersApplied(true);
            forceUpdate();
            setAppliedFiltersList(getAppliedFilters())
        } else {
            setFiltersApplied(false);
            setAppliedFiltersList([])
        }
    }, [materialCategoryValue, modelStatus, visibleToArtist, isRestoringFilters])

    // Trigger API call when filters change
    useEffect(() => {
        if (isRestoringFilters) return;
        // Reset offset and materials when filters change
        setOffset(0);
        setMaterials([]);
        setAllDataLoaded(false);
        // Fetch materials with new filters
        
        // Add a small delay to batch rapid filter changes and prevent race conditions
        const timeoutId = setTimeout(() => {
            fetchMaterials(true);
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }, [materialCategoryValue, modelStatus, visibleToArtist])

    const getMaterialPayload = (customOffset = null, customSearchValue = null, overrides = null) => {
        let payload = {
            "required_fields": ["id", "name", "brand_id", "category", "customer_username", "status", "last_modified_stamp", "company_id", 
            "platform", "material_files", "artist_visible"]
        }
        let filterString = '(is_hidden__not=true&&company_id__notexact=1)'
        
        // Use window.location.pathname directly
        if (artistSearchMaterial) {
            filterString += '&&(artist_visible__exact=true)';
        }

        const effectiveCategory = overrides && overrides.materialCategoryValue !== undefined ? overrides.materialCategoryValue : materialCategoryValue;
        const effectiveModelStatus = overrides && overrides.modelStatus !== undefined ? overrides.modelStatus : modelStatus;
        const effectiveVisibleToArtist = overrides && overrides.visibleToArtist !== undefined ? overrides.visibleToArtist : visibleToArtist;

        // Add category filter if categories are selected
        if (effectiveCategory.length > 0) {
            const categoryConditions = effectiveCategory.map(category => `category__exact='${category.replace(/'/g, "''")}'`).join('||');
            filterString += `&&(${categoryConditions})`;
        }

        // Add model status filter if selected
        if (effectiveModelStatus.length > 0) {
            const statusConditions = effectiveModelStatus.map(status => `status__in=[${status}]`).join('||');
            filterString += `&&(${statusConditions})`;
        }

        // Add visible to artist filter if enabled
        if (effectiveVisibleToArtist) {
            filterString += '&&(artist_visible__exact=true)';
        }

        const effectiveSearchValue = customSearchValue !== null ? customSearchValue : searchValue;
        if (effectiveSearchValue) {
            // Sanitize and split searchValue
            const parts = effectiveSearchValue.replace(/[^a-zA-Z0-9' ]/g, " ").split(" ").filter(Boolean);
            if (parts.length > 0) {
                const nameSearchConditions = parts.map(part => `name__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `&&((${nameSearchConditions})`;
                const categorySearchConditions = parts.map(part => `category__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `||(${categorySearchConditions})`;
                const brandSearchConditions = parts.map(part => `brand_id__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `||(${brandSearchConditions})`;

                // Only include parts that are valid integers for id search
                const idSearchParts = parts.filter(part => /^\d+$/.test(part));
                const idSearchConditions = idSearchParts.map(part => `id__exact='${part.replace(/'/g, "''")}'`).join('||');
                if (idSearchConditions) {
                    filterString += `||(${idSearchConditions})`;
                }
                filterString += ")";
            }
        }

        payload['filter_string'] = filterString
        payload['pagination_filters'] = {
            'limit': pageSize,
            'offset': customOffset !== null ? customOffset : offset
        }
        payload['order_by'] = 'last_modified_stamp desc'
        return payload;
    }

    const updateOffset = (offset) => {
        let newOffset = offset + pageSize;
        setOffset(newOffset);
    };

    const fetchMaterials = (isInitialLoad = false, customSearchValue = null, overrides = null) => {
        // Cancel any existing request
        if (currentRequestRef.current) {
            currentRequestRef.current.cancel();
        }
        
        // Create a new cancel token for this request
        const cancelTokenSource = axios.CancelToken.source();
        currentRequestRef.current = cancelTokenSource;
        
        if (isInitialLoad) {
            setIsLoadingMaterials(true);
            setOffset(0);
            // Use offset 0 for initial load
            let payload = getMaterialPayload(0, customSearchValue, overrides);
            return axios.post(ENVIRONMENT.CUSTOMER_MATERIAL_GET_BATCH, payload, {
                cancelToken: cancelTokenSource.token
            })
                .then(res => {
                    if (res.data) {
                        if (!res.data.length) {
                          setAllDataLoaded(true);
                          setIsLoadingMoreData(false);
                          setIsLoadingMaterials(false);
                          return;
                        }
                        setAllDataLoaded(false);
                        // Add entity_type: 'material' and last_modified to each object in res.data
                        const materialsWithEntityType = res.data.map(item => {
                            // Parse material_files if it's a JSON string
                            let parsedMaterialFiles = item.material_files;
                            if (typeof item.material_files === 'string') {
                                try {
                                    parsedMaterialFiles = JSON.parse(item.material_files);
                                } catch (error) {
                                    console.warn('Failed to parse material_files for item:', item.id, error);
                                    parsedMaterialFiles = [];
                                }
                            }
                            
                            return {
                                ...item,
                                material_files: parsedMaterialFiles,
                                entity_type: 'material',
                                last_modified: item.last_modified_stamp
                            };
                        });
                        
                        // Replace materials for initial load or filter changes
                        setMaterials(materialsWithEntityType);
                        // Update offset after initial load so next "load more" uses correct offset
                        updateOffset(0);
                        setIsLoadingMoreData(false);
                        setIsLoadingMaterials(false);
                    }
                })
                .catch(error => {
                    // Don't handle errors for cancelled requests
                    if (axios.isCancel(error)) {
                        console.log('Request cancelled');
                        return;
                    }
                    console.error('Error fetching materials:', error);
                    setIsLoadingMoreData(false);
                    setIsLoadingMaterials(false);
                });
        } else {
            setIsLoadingMoreData(true);
            // Use current offset for load more
            let payload = getMaterialPayload(undefined, customSearchValue, overrides);
            return axios.post(ENVIRONMENT.CUSTOMER_MATERIAL_GET_BATCH, payload, {
                cancelToken: cancelTokenSource.token
            })
                .then(res => {
                    if (res.data) {
                        if (!res.data.length) {
                          setAllDataLoaded(true);
                          setIsLoadingMoreData(false);
                          setIsLoadingMaterials(false);
                          return;
                        }
                        setAllDataLoaded(false);
                        // Add entity_type: 'material' and last_modified to each object in res.data
                        const materialsWithEntityType = res.data.map(item => {
                            // Parse material_files if it's a JSON string
                            let parsedMaterialFiles = item.material_files;
                            if (typeof item.material_files === 'string') {
                                try {
                                    parsedMaterialFiles = JSON.parse(item.material_files);
                                } catch (error) {
                                    console.warn('Failed to parse material_files for item:', item.id, error);
                                    parsedMaterialFiles = [];
                                }
                            }
                            
                            return {
                                ...item,
                                material_files: parsedMaterialFiles,
                                entity_type: 'material',
                                last_modified: item.last_modified_stamp
                            };
                        });
                        
                        // Append materials for "load more"
                        const tempMaterials = [...materials, ...materialsWithEntityType];
                        setMaterials(tempMaterials);
                        updateOffset(offset);
                        setIsLoadingMoreData(false);
                        setIsLoadingMaterials(false);
                    }
                })
                .catch(error => {
                    // Don't handle errors for cancelled requests
                    if (axios.isCancel(error)) {
                        console.log('Request cancelled');
                        return;
                    }
                    console.error('Error fetching materials:', error);
                    setIsLoadingMoreData(false);
                    setIsLoadingMaterials(false);
                });
        }
    }

    const setMaterialCategoryData = () => {
        let data = [];
        axios.post(ENVIRONMENT.ADMIN_MATERIAL_BATCH, {
        })
        .then(res => {
            if (res.data && Array.isArray(res.data)) {
                // Filter for top-level categories (parent_id is empty or null)
                const topLevelCategories = res.data.filter(item => 
                    !item.parent_id || item.parent_id === "" || item.parent_id === null
                );
                
                // Map to the format expected by the Select component
                data = topLevelCategories.map(category => ({
                    key: category.material_id,
                    title: category.name,
                    value: category.name,
                    label: category.name, // Add label for better Select compatibility
                }));
                
                setMaterialCategoriesData(data);
            }
        })
        .catch(error => {
            console.error('Error fetching material categories:', error);
            setMaterialCategoriesData([]);
        });
    }

    const changeSearchValue = (value) => {
        setSearchValue(value);
        // TODO: Implement search functionality
        console.log('Search value:', value);
    }

    const openFilters = () => {
        setFiltersDrawer(true);
    };

    const closeFiltersDrawer = () => {
        setFiltersDrawer(false);
    }

    const changeMaterialCategory = (value) => {
        setMaterialCategoryValue(value);
    }

    const changeModelStatus = (value) => {
        setModelStatus(value);
    }

    const getAppliedFilters = () => {
        let appliedFilters = [...materialCategoryValue, ...modelStatus]
        if (visibleToArtist) {
            appliedFilters.push('visible_to_artist');
        }
        appliedFilters = appliedFilters.map(item => item.toString().toLowerCase())
        return appliedFilters;
    }

    const closeTags = (entity, type) => {
        //remove the tag when this is clicked from showing result section
        if (type == 'material_category') {
            let arr = materialCategoryValue.filter(category => { return entity != category });
            changeMaterialCategory(arr)
        } else if (type == 'model_status') {
            let arr = modelStatus.filter(status => { return entity != status });
            setModelStatus(arr);
        } else if (type == 'visible_to_artist') {
            setVisibleToArtist(false);
        }
    }

    const resetFilters = () => {
        materialCategoryValue.length > 0 && materialCategoryValue.map((category) => {
            closeTags(category, 'material_category');
        });
        modelStatus.length > 0 && modelStatus.map((status) => {
            closeTags(status, 'model_status');
        })
        visibleToArtist && closeTags('visible_to_artist', 'visible_to_artist');
    };

    const getFilteredTags = () => {
        let filteredTags = <>
            {filtersApplied && <Col span={24} style={{ marginLeft: 10, marginTop: 20, textAlign: "left" }}>
                <h4 className='manrope f-14 black-55 italic'>Showing results for</h4>
                <div style={{ marginBottom: 20 }}>
                    {
                        materialCategoryValue.length > 0 && materialCategoryValue.map((category) => {
                            return <Tag onClose={() => closeTags(category, 'material_category')}
                                closable visible={true}
                                className='manrope f-12 black-55 tag-filter'>Category: <span className='manrope f-12 grey-77'>{category}</span></Tag>
                        })}

                    {
                        modelStatus.length > 0 && modelStatus.map((status) => {
                            return <Tag onClose={() => closeTags(status, 'model_status')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Model Status: <span className='manrope f-12 grey-77'>{FileConstants.MODEL_STATUS_STRING[status]}</span></Tag>
                        })}

                    {visibleToArtist && (
                        <Tag onClose={() => closeTags('visible_to_artist', 'visible_to_artist')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Visible to Artist</Tag>
                    )}
                </div>
            </Col>}
        </>

        return filteredTags;
    }

    // Add onLoadMore handler
    const handleLoadMore = () => {
        if (!isLoadingMoreData && !allDataLoaded) {
            fetchMaterials();
        }
    };

    const handleSearchIconClick = () => {
        if(searchValue && searchValue.trim() !== '')
        {     
            setOffset(0);
            setMaterials([]);
            setAllDataLoaded(false);
            fetchMaterials(true);
        }
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setOffset(0);
        setMaterials([]);
        setAllDataLoaded(false);
        // Remove search parameter from URL
        const params = new URLSearchParams(window.location.search);
        params.delete("search");
        window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
        fetchMaterials(true, '', { searchValue: '' }); // Pass empty string as search value
    };

    useEffect(() => {
        console.log(selectedAdd, selectedRemove)
    }, [selectedAdd, selectedRemove])

    const handleAddToArtistSearch = () => {
        setActionLoader(true);
        let payload = {
            "add_ids": selectedAdd,
            "remove_ids": selectedRemove,
            "entity": "material"
        }

        axios.post(ENVIRONMENT.UPDATE_ARTIST_VISIBLE, payload)
            .then(res => {
                console.log(res);
                setActionLoader(false);
                message.success('Materials added to artist search');
                window.location.reload();
            })
            .catch(error => {
                console.error('Error adding to artist search:', error);
                setActionLoader(false);
            });
    }

    return (
        <>
        { isLoadingMaterials && offset == 0 ? 
            <div className="justify-in-center">
                <DottedLoader />
            </div>
        :
        <div className="material-search">
            <Row className="justify-space-between" style={{ marginTop: "20px", marginBottom: 20 }}>
                <Col lg={10} xl={10}>
                    <div className='manrope f-24 w-700' style={{ display: 'flex', alignItems: "flex-end" }}>
                        Material Library
                    </div>
                </Col>
                <Col lg={14} xl={14} className="justify-in-end">
                    <div className="search-bar-width mr-8">
                        <Form>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <Input 
                                    placeholder="Search All Materials" 
                                    value={searchValue} 
                                    onChange={e => { changeSearchValue(e.target.value); }} 
                                    className="manrope f-14 mat-search-bar custom-input-id"
                                    style={{ paddingRight: searchValue ? '84px' : '48px' }}
                                    onPressEnter={handleSearchIconClick}
                                />
                                {searchValue && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: 42,
                                            top: 3,
                                            height: '87%',
                                            width: '42px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            zIndex: 11,
                                            background: '#fff',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0,
                                        }}
                                        onClick={handleClearSearch}
                                    >
                                        <CloseCircleFilled style={{ color: '#aaa', fontSize: 18 }} />
                                    </div>
                                )}
                                <div className="suffix-container" style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#276DD7', borderTopRightRadius: '4px', borderBottomRightRadius: '4px', cursor: 'pointer', zIndex: 10 }} onClick={handleSearchIconClick}>
                                    <ArrowRightOutlined className="custom-icon" />
                                </div>
                            </div>
                        </Form>
                    </div>
                    <div>
                        <Tooltip title={<span className="manrope f-12 white">Apply Filters</span>}>
                            <span>
                                <div className={"icon-button-container mr-8"} onClick={openFilters}>
                                    <FilterOutlined />
                                </div>
                            </span>
                        </Tooltip>
                    </div>
                </Col>
            </Row>
            {getFilteredTags()}
            <MaterialList 
                data={materials} 
                onLoadMore={handleLoadMore} 
                searchMode={true} 
                selectable={true}
                selected={selectedAdd}
                setSelected={setSelectedAdd}
                setSelectedElementDetials={setSelectedElementDetials}
                selectedRemove={selectedRemove}
                setSelectedRemove={setSelectedRemove}
                artistSearch={artistSearchMaterial}
            />

            {/* Bottom menu logic */}
            {selectedAdd.length > 0 || selectedRemove.length > 0 ? (
                <div className="bottom-menu" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Selected count on the left */}
                    <div className='manrope black-d9 f-16 ml-168' style={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleFilled className='mr-2' />   {selectedAdd.length > 0 ? `${selectedAdd.length} material(s) selected` : ''} {selectedAdd.length > 0 && selectedRemove.length > 0 ? ', ' : ''} {selectedRemove.length > 0 ? `${selectedRemove.length} material(s) removed` : ''}
                    </div>
                    {/* Loading message in the center if loading more */}
                    {isLoadingMoreData && (
                        <div className='manrope f-16' style={{ color: '#276DD7', padding: 10, display: 'flex', alignItems: 'center' }}>
                            <div className="more-item-loading-container justify-in-center">
                                <Spin indicator={antIcon} tip="Loading more materials" />
                            </div>
                        </div>
                    )}
                    {/* Button on the right */}
                    <Button
                        className="product-btn approve-button f-14 ml-16 br-4"
                        onClick={handleAddToArtistSearch}
                    >
                        {actionLoader ? <LoadingOutlined /> : ""}
                        Save Changes to Artist Search
                    </Button>
                </div>
            ) : null}
            {/* If no materials are selected, keep the current loading more message */}
            {selectedAdd.length === 0 && isLoadingMoreData && (
                <div className="more-item-loading-container justify-in-center">
                    <Spin indicator={antIcon} tip="Loading more materials" />
                </div>
            )}
        </div>}
        <Drawer
                width={360}
                visible={filtersDrawer}
                onClose={closeFiltersDrawer}
                placement="right">
                <div style={{ padding: 20 }}>
                    <h2 className="manrope f-18 black-14 w-700">Filters</h2>
                    <div>
                        <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>
                            Material Category
                        </h5>
                        <Select
                            value={materialCategoryValue}
                            showSearch={true}
                            className="tree-select-material f-14 filter bg-light"
                            style={{ width: '100%' }}
                            mode="multiple"
                            placeholder="Search and select category"
                            onChange={changeMaterialCategory}
                            filterOption={(input, option) =>
                                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {materialCategoriesData.map((category) => (
                                <Select.Option
                                    key={category.key}
                                    className="manrope f-10"
                                    value={category.value}
                                    label={category.label}
                                >
                                    {category.title}
                                </Select.Option>
                            ))}
                        </Select>


                        {
                            !artistSearchMaterial &&
                            <>
                            <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Model Status</h5>
                                <Select
                                    className="tree-select-material f-14 filter bg-light"
                                    value={modelStatus}
                                    onChange={changeModelStatus}
                                    showSearch
                                    placeholder="Search and select model status"
                                    mode="multiple"
                                >
                                    {Object.keys(FileConstants.MODEL_STATUS_STRING).map((key) => (
                                        <Select.Option
                                            key={key}
                                            className="manrope f-10"
                                            value={parseInt(key)}
                                        >
                                            {FileConstants.MODEL_STATUS_STRING[key]}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Visible to Artist</h5>
                                <Switch
                                    checked={visibleToArtist}
                                    onChange={setVisibleToArtist}
                                />
                            </>
                        }
                    </div>
                </div>
            </Drawer>
        </>
    )
}

export default MaterialSearch;